import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/noir/Navbar";
import Footer from "@/components/noir/Footer";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

function fmt12(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${suffix}`;
}
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function BookAppointment() {
  const [params] = useSearchParams();
  const preselected = params.get("services");
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set(preselected ? preselected.split("|") : []));
  const [date, setDate] = useState(undefined);
  const [availability, setAvailability] = useState({ slots: [], unavailable: [], whole_day_blocked: false });
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => { api.get("/services").then((r) => setCategories(r.data.categories || [])); }, []);
  useEffect(() => {
    if (!date) return;
    setTime("");
    api.get(`/availability?date_str=${toISODate(date)}`).then((r) => setAvailability(r.data));
  }, [date]);

  const toggleService = (s) => {
    const c = new Set(selected);
    c.has(s) ? c.delete(s) : c.add(s);
    setSelected(c);
  };

  const canContinueStep1 = selected.size > 0;
  const canContinueStep2 = !!date && !!time;
  const canSubmit = name.trim().length >= 2 && /^\+?\d{10,13}$/.test(mobile.replace(/\s/g, ""));

  const submit = async () => {
    if (!canSubmit) { toast.error("Please enter a valid name and 10-digit mobile."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        customer_name: name.trim(),
        mobile: mobile.trim(),
        services: Array.from(selected),
        appointment_date: toISODate(date),
        appointment_time: time,
        note: note.trim(),
      });
      toast.success("Appointment request received");
      // Open owner's WhatsApp in a new tab with a prefilled request so the studio owner is notified.
      // Runs even when WhatsApp Business API creds are not configured (fallback wa.me link).
      if (data.owner_notify_url) {
        try { window.open(data.owner_notify_url, "_blank", "noopener,noreferrer"); } catch {}
      }
      navigate(`/confirmation/${data.appointment.reference}`, { state: { owner_notify_url: data.owner_notify_url } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = ["Services", "Date & Time", "Your Details"][step - 1];

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-[#F7F3EC]">
      <Navbar />
      <section className="pt-32 pb-20 px-5 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[#A1A1AA] hover:text-[#B8935A]" data-testid="back-home"><ArrowLeft size={14} /> Back to Home</Link>
          <p className="eyebrow mt-8">Book · Step {step} of 3</p>
          <h1 className="serif text-4xl md:text-6xl mt-3 leading-tight">{stepLabel}</h1>
          <div className="mt-6 flex gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`h-px flex-1 transition-colors ${n <= step ? "bg-[#B8935A]" : "bg-[rgba(247,243,236,0.15)]"}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="mt-12 space-y-14" data-testid="step-services">
              {categories.map((cat) => (
                <div key={cat.category}>
                  <div className="flex items-baseline justify-between gap-6 mb-6">
                    <h3 className="serif text-2xl">{cat.category}</h3>
                    <div className="flex-1 h-px bg-[rgba(184,147,90,0.25)]" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {cat.items.map((s) => {
                      const active = selected.has(s);
                      return (
                        <button
                          key={s}
                          onClick={() => toggleService(s)}
                          data-testid={`booking-service-${s}`}
                          className={`text-left p-5 border transition-colors ${active ? "border-[#B8935A] bg-[#B8935A]/10" : "border-[rgba(247,243,236,0.12)] hover:border-[#B8935A]"}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-5 h-5 border flex items-center justify-center shrink-0 ${active ? "bg-[#B8935A] border-[#B8935A]" : "border-[#A1A1AA]"}`}>
                              {active && <Check size={12} className="text-[#0B0B0B]" strokeWidth={3} />}
                            </div>
                            <span className="serif text-lg">{s}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="pt-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                <p className="text-sm text-[#A1A1AA]">{selected.size} selected</p>
                <button disabled={!canContinueStep1} onClick={() => setStep(2)} data-testid="step1-continue" className={`btn-primary ${!canContinueStep1 && "opacity-40 pointer-events-none"}`}>
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-12 grid md:grid-cols-2 gap-10" data-testid="step-datetime">
              <div>
                <p className="eyebrow mb-4">Choose Date</p>
                <div className="border border-[rgba(247,243,236,0.12)] p-3 bg-[#0E0E0E] inline-block" data-testid="date-picker-wrap">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </div>
                {availability.whole_day_blocked && (
                  <p className="mt-4 text-sm text-red-400">This date is fully unavailable. Please choose another.</p>
                )}
              </div>
              <div>
                <p className="eyebrow mb-4">Choose Time</p>
                {!date && <p className="text-sm text-[#A1A1AA]">Select a date first.</p>}
                {date && (
                  <div className="grid grid-cols-3 gap-2" data-testid="time-slot-grid">
                    {availability.slots.map((s) => {
                      const unavailable = availability.unavailable.includes(s);
                      const active = time === s;
                      return (
                        <button
                          key={s}
                          disabled={unavailable}
                          onClick={() => setTime(s)}
                          data-testid={`time-slot-${s}`}
                          className={`py-3 text-xs tracking-[0.15em] uppercase border transition-colors ${
                            unavailable ? "border-[rgba(247,243,236,0.06)] text-[#5a5a5f] line-through" :
                            active ? "border-[#B8935A] bg-[#B8935A] text-[#0B0B0B]" :
                            "border-[rgba(247,243,236,0.15)] hover:border-[#B8935A]"
                          }`}
                        >
                          {fmt12(s)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between pt-4">
                <button onClick={() => setStep(1)} className="text-xs tracking-[0.28em] uppercase text-[#A1A1AA] hover:text-[#B8935A]" data-testid="step2-back">← Back</button>
                <button disabled={!canContinueStep2} onClick={() => setStep(3)} data-testid="step2-continue" className={`btn-primary ${!canContinueStep2 && "opacity-40 pointer-events-none"}`}>
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-12 max-w-xl" data-testid="step-details">
              <div className="space-y-8">
                <div>
                  <label className="eyebrow block mb-3">Full Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name" className="w-full bg-transparent border-b border-[rgba(247,243,236,0.2)] focus:border-[#B8935A] outline-none py-3 text-lg placeholder:text-[#3f3f42]" placeholder="Your name" />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Mobile / WhatsApp Number</label>
                  <input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="input-mobile" inputMode="tel" className="w-full bg-transparent border-b border-[rgba(247,243,236,0.2)] focus:border-[#B8935A] outline-none py-3 text-lg placeholder:text-[#3f3f42]" placeholder="e.g. 9876543210" />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Note (optional)</label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-note" rows="3" className="w-full bg-transparent border border-[rgba(247,243,236,0.15)] focus:border-[#B8935A] outline-none p-4 text-base placeholder:text-[#3f3f42]" placeholder="Any special request?" />
                </div>
              </div>

              <div className="mt-10 border border-[rgba(184,147,90,0.25)] p-6 bg-[#0E0E0E]">
                <p className="eyebrow mb-4">Summary</p>
                <div className="grid gap-3 text-sm">
                  <div className="flex justify-between gap-6"><span className="text-[#A1A1AA]">Services</span><span className="text-right">{Array.from(selected).join(", ")}</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Date</span><span>{date && toISODate(date)}</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Time</span><span>{time && fmt12(time)}</span></div>
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                <button onClick={() => setStep(2)} className="text-xs tracking-[0.28em] uppercase text-[#A1A1AA] hover:text-[#B8935A]" data-testid="step3-back">← Back</button>
                <button disabled={!canSubmit || submitting} onClick={submit} data-testid="submit-booking" className={`btn-primary ${(!canSubmit || submitting) && "opacity-40 pointer-events-none"}`}>
                  {submitting ? "Sending..." : "Request Appointment"} <ArrowRight size={14} />
                </button>
              </div>
              <p className="mt-6 text-xs text-[#A1A1AA] leading-relaxed">Your appointment is a request. We will confirm on WhatsApp once approved.</p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
